import { ExclamationCircleOutlined } from "@ant-design/icons";
import Drawer from "@material-ui/core/Drawer";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ChatOutlinedIcon from "@material-ui/icons/ChatOutlined";
import KeyboardBackspaceOutlinedIcon from "@material-ui/icons/KeyboardBackspaceOutlined";
import { MessagingChannelFeature as Feature } from "@whatsper/texterchat-common";
import Button from "antd/es/button";
import classnames from "classnames";
import { formatNumber, isValidPhoneNumber, parseNumber } from "libphonenumber-js";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { Trans } from "react-i18next";
import { useHistory } from "react-router-dom";
import { MyInput } from "../Base/Form";
import { useStore } from "../../store/rootStore";
import ChannelSelect from "../Chat/ChatsList/ChannelSelect";
import { getDirection } from "../../helpers";
import "./MessageDrawer.scss";
const MessageDrawer = ({ setChat, toggleMessageDrawer, isOpen }) => {
    const store = useStore();
    const history = useHistory();
    const [accounts, setAccounts] = useState([]);
    const [account, setAccount] = useState(null);
    const [chatId, setChatId] = useState('');
    const [phoneIdSupported, setPhoneIdSupported] = useState(false);
    const [emailSupported, setEmailSupported] = useState(false);
    const [validInput, setValidInput] = useState(false);
    const theme = account ? store.channels.themes[account.accountId] : null;
    const isSendDisabled = !validInput || chatId.trim().length === 0;
    const onChannelIdSelected = (channel) => {
        setAccount(accounts.find(({ accountId, channel: c }) => {
            return c === (channel === null || channel === void 0 ? void 0 : channel.name) && accountId === (channel === null || channel === void 0 ? void 0 : channel.id);
        }) || null);
    };
    // Populate accounts list with accounts that support contactGlobalId feature
    // additionally mapping with adapter features,
    // when accounts or adapters change
    useEffect(() => {
        setAccounts(store.channels.accounts.map((account) => {
            const adapter = store.channels.adaptersByName[account.adapterName];
            return Object.assign(Object.assign({}, account), { features: adapter ? adapter.features : [] });
        })
            .filter(({ features }) => {
            return features.includes(Feature.contactGlobalId);
        }));
    }, [store.channels.accounts.length, store.channels.adaptersByName]);
    // Set current account to value selected in search filter,
    // or otherwise set to default one or first account in list
    useEffect(() => {
        let a = null;
        if (store.chatsStore.searchFilter.channel) {
            const { id, name } = store.chatsStore.searchFilter.channel;
            a = accounts.find(({ accountId, channel }) => {
                return id === accountId && channel === name;
            }) || null;
        }
        if (!a && accounts.length) {
            a = accounts.find(({ isDefault }) => isDefault) || accounts[0];
        }
        setAccount(a);
    }, [store.chatsStore.searchFilter.channel, accounts]);
    useEffect(() => {
        if (account) {
            setPhoneIdSupported(account.features.includes(Feature.contactPhoneNumberId));
            setEmailSupported(account.features.includes(Feature.contactEmailId));
        }
        else {
            setEmailSupported(false);
            setPhoneIdSupported(false);
        }
    }, [account]);
    useEffect(() => {
        let isValid = false;
        if (phoneIdSupported) {
            isValid = !chatId.length || isValidPhoneNumber(chatId, store.defaultCountry);
        }
        if (emailSupported && !isValid) { // prevent override of phone number validation
            isValid = !chatId.length || isValidEmail(chatId);
        }
        if (!phoneIdSupported && !emailSupported) {
            isValid = true;
        }
        setValidInput(isValid);
    }, [chatId, phoneIdSupported, emailSupported]);
    const handleChange = (value) => {
        const serilizedNumber = value.replace(/[^\x21-\x7E]/g, "");
        setChatId(serilizedNumber);
    };
    const handleEnter = (e) => {
        if (e.key === 'Enter') {
            onSend();
        }
    };
    const getInvalidInputErrorKey = () => {
        if (phoneIdSupported && emailSupported) {
            return "invalidPhoneOrEmail";
        }
        if (phoneIdSupported) {
            return "incorrectNumber";
        }
        if (emailSupported) {
            return "invalidEmail";
        }
        return "incorrectNumber"; // should never happen, but just in case
    };
    const onSend = () => {
        if (chatId && validInput) {
            let id = chatId;
            if (phoneIdSupported) {
                const phonenumber = parseNumber(chatId, store.defaultCountry);
                id = formatNumber(phonenumber, "E.164").replace(/[^\d]/g, '');
            }
            if (emailSupported && !id) { // prevent override of phone number validation.
                id = chatId;
            }
            if (account) {
                setChat(id, { id: account.accountId, name: account.channel });
                history.push(`/contact/${account.channel}/${account.accountId}/${id}`);
                toggleMessageDrawer(false);
            }
            // reset input value
            setChatId('');
        }
    };
    function isValidEmail(email) {
        if (!email)
            return false;
        const trimmed = email.trim();
        if (trimmed.length === 0)
            return false;
        const emailRegex = /^.+@.+$/;
        return emailRegex.test(trimmed);
    }
    function onHide() {
        toggleMessageDrawer(false);
        setChatId('');
    }
    return (React.createElement(Drawer, { className: classnames("message-drawer", {
            "rtl": getDirection() === "rtl",
        }), open: isOpen, onClose: onHide, dir: getDirection(), anchor: getDirection() === "rtl" ? "right" : "left" },
        React.createElement("div", { className: classnames("message-drawer__back", {
                "rtl": getDirection() === "rtl",
            }), onClick: onHide },
            React.createElement(ListItemIcon, null,
                React.createElement(KeyboardBackspaceOutlinedIcon, { fontSize: "small" })),
            React.createElement("span", null,
                React.createElement(Trans, { i18nKey: "sendNewMessage" }))),
        React.createElement("div", { className: "message-drawer__container" },
            React.createElement("div", null,
                React.createElement("div", { className: "message-drawer__header" },
                    React.createElement(ChatOutlinedIcon, { className: "message-drawer__icon", fontSize: "large" })),
                React.createElement("div", { className: "message-drawer__content" },
                    store.channels.accounts.length > 1 &&
                        React.createElement("div", { className: "message-drawer__box" },
                            React.createElement("div", { className: "message-drawer__input-title" },
                                React.createElement(Trans, { i18nKey: "sendFrom" })),
                            React.createElement(ChannelSelect, { activeChannel: account ? { name: account.channel, id: account.accountId } : null, onChannelClick: onChannelIdSelected, className: "message-drawer__channelSelect", accounts: accounts, initSearch: false, showAll: false })),
                    React.createElement("div", { className: "message-drawer__box" },
                        React.createElement("div", { className: "message-drawer__input-title" },
                            React.createElement("div", null,
                                React.createElement(Trans, { i18nKey: "sendNewMessageTo" })),
                            !validInput ? (React.createElement("div", { className: "message-drawer__error" },
                                React.createElement(Trans, { i18nKey: getInvalidInputErrorKey() }))) : null),
                        React.createElement(MyInput, { onChangeHandler: handleChange, value: chatId, className: "message-drawer__input", onKeyDown: handleEnter, size: "middle", autoFocus: true })),
                    React.createElement(Button, Object.assign({ type: "primary", className: "message-drawer__button big-button resolve-button", onClick: onSend, disabled: isSendDisabled }, (theme && !isSendDisabled) ? { style: { backgroundColor: theme === null || theme === void 0 ? void 0 : theme.color } } : {}),
                        React.createElement(Trans, { i18nKey: "send" })),
                    store.channels.accounts.length !== accounts.length ? (React.createElement("div", { className: classnames("message-drawer__moreInfo", { rtl: getDirection() === "rtl" }) },
                        React.createElement(ExclamationCircleOutlined, { className: "info-circle" }),
                        React.createElement("p", { className: "info-text" },
                            React.createElement(Trans, { i18nKey: "nonGlobalIdChannelsHidden" })))) : (React.createElement("div", { className: "message-placeholder" })))))));
};
export default observer(MessageDrawer);
